import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login, getAuthProviders } from '../api';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const [shake, setShake] = useState(false);
  const [providers, setProviders] = useState([]);
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const cardRef = useRef(null);

  // Load SSO providers
  useEffect(() => {
    getAuthProviders()
      .then(setProviders)
      .catch(() => {}); // silently fail if providers endpoint unavailable
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const emailValid = !emailTouched || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password, rememberMe);
      setFailedAttempts(0);
      loginUser(data);
      navigate('/');
    } catch (err) {
      const attempts = failedAttempts + 1;
      setFailedAttempts(attempts);
      setError(err.message);
      triggerShake();
      if (attempts >= 3) {
        const seconds = Math.min(10 * Math.pow(2, attempts - 3), 60);
        setCooldown(seconds);
      }
    } finally {
      setLoading(false);
    }
  };

  const ssoIcon = (providerId) => {
    if (providerId === 'google') {
      return (
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      );
    }
    if (providerId === 'microsoft') {
      return (
        <svg width="18" height="18" viewBox="0 0 21 21">
          <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
          <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
          <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
        </svg>
      );
    }
    return null;
  };

  return (
    <div className="auth-container">
      <div className={`auth-card${shake ? ' auth-shake' : ''}`} ref={cardRef}>
        <div className="auth-logo">
          <img src="/logo-pocketrams.png" alt="PocketRAMS" />
        </div>
        <p className="auth-subtitle">Risk Assessment Management System</p>

        {error && <div className="alert alert-error">{error}</div>}
        {cooldown > 0 && (
          <div className="alert alert-error">
            Too many failed attempts. Please wait {cooldown}s before trying again.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <div className="input-icon-wrapper">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 4L12 13 2 4" />
              </svg>
              <input
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                required
                placeholder="you@example.com"
                className={!emailValid ? 'input-invalid' : ''}
              />
            </div>
            {!emailValid && <small className="field-error">Please enter a valid email address</small>}
          </div>
          <div className="form-group">
            <div className="label-row">
              <label>Password</label>
              <Link to="/reset-password" className="forgot-link">Forgot password?</Link>
            </div>
            <div className="input-icon-wrapper">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <label className="remember-me">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>Remember me for 30 days</span>
          </label>

          <button
            type="submit"
            className="btn btn-primary btn-full auth-submit"
            disabled={loading || cooldown > 0}
          >
            {loading ? 'Signing in...' : cooldown > 0 ? `Wait ${cooldown}s...` : 'Sign In'}
          </button>
        </form>

        {providers.length > 0 && (
          <>
            <div className="auth-divider">
              <span>or</span>
            </div>
            <div className="sso-buttons">
              {providers.map((p) => (
                <a
                  key={p.id}
                  href={`${API_BASE.replace('/api', '')}/oauth2/authorization/${p.id}`}
                  className="btn btn-sso"
                >
                  {ssoIcon(p.id)}
                  <span>Sign in with {p.name}</span>
                </a>
              ))}
            </div>
          </>
        )}
      </div>
      <p className="auth-footer">&copy; 2026 Pocket Data Ltd. All rights reserved.</p>
    </div>
  );
}
